import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Send, Undo2, Loader2, X } from 'lucide-react';
import { aiApi } from '../../api/endpoints/ai';
import { PortalDropdown } from './PortalDropdown';

type ContentType = 'product' | 'blog' | 'page';

interface AiSuggestion {
  key: string;
  label: string;
  prompt: string;
}

interface AiWritingAssistantProps {
  currentContent: string;
  /** Title of the item being edited (product name, blog title, page title) */
  itemTitle?: string;
  /** @deprecated Use itemTitle instead */
  productTitle?: string;
  onContentGenerated: (content: string) => void;
  /** Type of content being written - affects default suggestions */
  contentType?: ContentType;
  /** Override default suggestions with custom ones */
  suggestions?: AiSuggestion[];
}

const PRODUCT_SUGGESTIONS: AiSuggestion[] = [
  { key: 'professional', label: 'ai.professional', prompt: 'Rewrite this product description in a professional, polished tone' },
  { key: 'improve', label: 'ai.improve', prompt: 'Improve this product description to be more engaging and compelling' },
  { key: 'concise', label: 'ai.concise', prompt: 'Make this product description more concise while keeping key information' },
  { key: 'features', label: 'ai.features', prompt: 'Rewrite this as a features-focused product description with bullet points' },
  { key: 'seoOptimized', label: 'ai.seoOptimized', prompt: 'Optimize this product description for SEO with relevant keywords' },
];

const BLOG_SUGGESTIONS: AiSuggestion[] = [
  { key: 'professional', label: 'ai.professionalArticle', prompt: 'Write a professional, well-structured article about this topic' },
  { key: 'improve', label: 'ai.improve', prompt: 'Improve and polish this content to be more engaging and well-written' },
  { key: 'concise', label: 'ai.concise', prompt: 'Make this content shorter and more concise while keeping the key points' },
  { key: 'introduction', label: 'ai.introduction', prompt: 'Add an engaging introduction to this content' },
  { key: 'seoOptimized', label: 'ai.seoOptimized', prompt: 'Optimize this content for SEO with relevant keywords and structure' },
  { key: 'keyTakeaways', label: 'ai.keyTakeaways', prompt: 'Convert this content to bullet points and key takeaways' },
];

const PAGE_SUGGESTIONS: AiSuggestion[] = [
  { key: 'professional', label: 'ai.professional', prompt: 'Rewrite this page content in a professional, polished tone' },
  { key: 'improve', label: 'ai.improve', prompt: 'Improve this page content to be clearer and more engaging' },
  { key: 'concise', label: 'ai.concise', prompt: 'Make this page content more concise while keeping key information' },
  { key: 'seoOptimized', label: 'ai.seoOptimized', prompt: 'Optimize this page content for SEO with relevant keywords' },
];

const SUGGESTIONS_BY_TYPE: Record<ContentType, AiSuggestion[]> = {
  product: PRODUCT_SUGGESTIONS,
  blog: BLOG_SUGGESTIONS,
  page: PAGE_SUGGESTIONS,
};

const SUGGESTION_LABEL_DEFAULTS: Record<string, string> = {
  'ai.professional': 'Professional',
  'ai.professionalArticle': 'Professional Article',
  'ai.improve': 'Improve',
  'ai.concise': 'Concise',
  'ai.features': 'Features',
  'ai.seoOptimized': 'SEO-Optimized',
  'ai.introduction': 'Introduction',
  'ai.keyTakeaways': 'Key Takeaways',
};

export function AiWritingAssistant({
  currentContent,
  itemTitle,
  productTitle,
  onContentGenerated,
  contentType = 'product',
  suggestions: customSuggestions,
}: AiWritingAssistantProps) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [previousContent, setPreviousContent] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const title = itemTitle || productTitle;
  const activeSuggestions = customSuggestions || SUGGESTIONS_BY_TYPE[contentType];

  // Close panel on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const mutation = useMutation({
    mutationFn: (prompt: string) =>
      aiApi.write(storeId!, { prompt, currentContent, productTitle: title, contentType }),
    onSuccess: (data) => {
      setPreviousContent(currentContent);
      onContentGenerated(data.content);
      setIsOpen(false);
      setCustomPrompt('');
    },
  });

  const handleSuggestion = (suggestion: AiSuggestion) => {
    mutation.mutate(suggestion.prompt);
  };

  const handleCustomSubmit = () => {
    const prompt = customPrompt.trim();
    if (!prompt) return;
    mutation.mutate(prompt);
  };

  const handleRevert = () => {
    if (previousContent !== null) {
      onContentGenerated(previousContent);
      setPreviousContent(null);
    }
  };

  return (
    <div className="relative flex items-center gap-2">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={mutation.isPending}
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-accent bg-accent/10 border border-accent/20 hover:bg-accent/15 hover:border-accent/30 transition-colors disabled:opacity-50"
      >
        {mutation.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        {t('ai.assist', 'AI Assist')}
      </button>

      {previousContent !== null && !mutation.isPending && (
        <button
          type="button"
          onClick={handleRevert}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-text-secondary bg-bg-tertiary border border-border-default hover:bg-bg-hover transition-colors"
        >
          <Undo2 className="h-3 w-3" />
          {t('ai.revert', 'Revert')}
        </button>
      )}

      <PortalDropdown
        anchorEl={buttonRef.current}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        align="start"
        placement="top"
        width={320}
        backdrop={false}
        className="rounded-lg border border-border-default bg-bg-secondary shadow-lg"
      >
        <div ref={panelRef}>
          <div className="flex items-center justify-between border-b border-border-default px-3 py-2">
            <span className="text-xs font-medium text-text-secondary">
              {t('ai.suggestions', 'Quick suggestions')}
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded p-0.5 text-text-muted hover:bg-bg-hover"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="p-3 space-y-3">
            {/* Suggestion chips */}
            <div className="flex flex-wrap gap-1.5">
              {activeSuggestions.map((suggestion) => (
                <button
                  key={suggestion.key}
                  type="button"
                  onClick={() => handleSuggestion(suggestion)}
                  disabled={mutation.isPending}
                  className="rounded-full border border-border-default bg-bg-field px-3 py-1 text-xs text-text-secondary hover:border-accent/30 hover:text-accent hover:bg-accent/5 transition-colors disabled:opacity-50"
                >
                  {t(suggestion.label, SUGGESTION_LABEL_DEFAULTS[suggestion.label] || suggestion.key)}
                </button>
              ))}
            </div>

            {/* Custom prompt input */}
            <div className="flex gap-1.5">
              <input
                type="text"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCustomSubmit();
                  }
                }}
                placeholder={t('ai.customPrompt', 'Custom prompt...')}
                disabled={mutation.isPending}
                className="flex-1 rounded-md border border-border-default bg-bg-field px-2.5 py-1.5 text-xs text-text-primary placeholder:text-text-muted outline-none focus:border-accent disabled:opacity-50"
              />
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={mutation.isPending || !customPrompt.trim()}
                className="rounded-md bg-accent px-2 py-1.5 text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                {mutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
              </button>
            </div>

            {/* Error */}
            {mutation.isError && (
              <p className="text-xs text-danger">
                {t('ai.error', 'Failed to generate. Please try again.')}
              </p>
            )}
          </div>
        </div>
      </PortalDropdown>
    </div>
  );
}
